import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip,
  Divider,
  useTheme,
  Paper,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { executeAnyMutation, fetchJsonDynamic } from "../../../../data/DynamicServiceNew/jsondynamicservice";
import { AccountsandFinanceurl, customerservice, setupManagementUrl } from "../../../../config";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const base_url = AccountsandFinanceurl.uri;
const customers_url = customerservice.uri;
const setup_url = setupManagementUrl.uri;

const AccountTransferForm = (props) => {
  const theme = useTheme(); // Access theme for dynamic styling
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const CustomerId = props.data.Id;

  const [transactionTypeData, setTransactionTypeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransactionType, setSelectedTransactionType] = useState("");
  const [recipientMobile, setRecipientMobile] = useState("");
  const [recipientId, setRecipientId] = useState(null);
  const [recipientName, setRecipientName] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [LoadingSearch, setLoadingSearch] = useState(false);
  const [recipientOptions, setRecipientOptions] = useState([]); // Holds recipients found via mobile search
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchTransactionTypes() {
      try {
        const transactionTypeResponse = await fetchJsonDynamic("allTransactionTypes", null, null, setup_url);
        if (transactionTypeResponse?.results) {
          const results = JSON.parse(transactionTypeResponse?.results);
          setTransactionTypeData(results || []);
        }
      } catch (error) {
        console.error("Error fetching transaction types:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchTransactionTypes();
  }, []);

  const handleTransactionTypeChange = (e) => {
    const transactionType = Number(e.target.value);
    setSelectedTransactionType(transactionType);
  
    if (transactionType === 3 || transactionType === 4) {
      // Preset recipient details with the customer's details for transaction type 3 or 4
      setRecipientMobile(props.data.MobileNumber || ""); // Use customer's mobile number
      setRecipientId(props.data.Id || null); // Use customer's ID
      setRecipientName(`${props.data.FirstName || ""} ${props.data.OtherNames || ""}`.trim()); // Use customer's name
    } else {
      // Reset recipient details for other transaction types
      setRecipientMobile("");
      setRecipientId(null);
      setRecipientName("");
    }
  };
  
  const handleRecipientSearch = async (mobile) => {
  try {
    if (!mobile || mobile.length < 10) {
      setRecipientId(null);
      setRecipientName("");
      setRecipientOptions([]); // Clear previous options
      Swal.fire("Error", "Please enter a valid mobile number.", "error");
      return;
    }

    setLoadingSearch(true);

    const query = `
      query {
        customersByColumnValues(customer: {mobileNumber: "${mobile}"}) {
          error
          results
        }
      }
    `;
    const token = localStorage.getItem("token");
    const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;

    const response = await axios.post(
      customers_url,
      { query },
      {
        headers: {
          Authorization: formattedToken,
          "Content-Type": "application/json",
        },
      }
    );

    const data = response?.data?.data?.customersByColumnValues?.results;

    if (data) {
      const parsedResults = JSON.parse(data);
      console.log("recipient customer", parsedResults)

      if (parsedResults.length) {

        setRecipientOptions(parsedResults); // Set recipients in dropdown


        const foundCustomer = parsedResults[0];
        setRecipientId(foundCustomer.Id);
        setRecipientName(`${foundCustomer.FirstName} ${foundCustomer.OtherNames}`);
        Swal.fire("Success", `Recipient found: ${foundCustomer.FirstName} ${foundCustomer.OtherNames}`, "success");
      } else {
        setRecipientId(null);
        setRecipientName("");
        setRecipientOptions([]); // Clear options if no customer found
        Swal.fire("Error", "No customer found with this mobile number.", "error");
      }
    } else if (data?.error) {
      setRecipientId(null);
      setRecipientName("");
      setRecipientOptions([]); // Clear options on error
      Swal.fire("Error", data.error || "Unexpected error occurred.", "error");
    }
  } catch (error) {
    setRecipientId(null);
    setRecipientName("");
    setRecipientOptions([]); // Clear options on error
    Swal.fire("Error", "Failed to search recipient. Please try again.", "error");
  } finally {
    setLoadingSearch(false);
  }
};

  const handleMobileChange = (e) => {
    const mobile = e.target.value;
    setRecipientMobile(mobile);

    if (mobile.length >= 10) {
      handleRecipientSearch(mobile);
    } else {
      setRecipientId(null);
      setRecipientName("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const mutationData = {
        p_CustomerIdFrom: parseInt(CustomerId),
        p_CustomerIdTo: recipientId,
        p_TransferAmount: parseFloat(amount),
        p_TransactionTypeId: parseInt(selectedTransactionType),
      };

      const response = await executeAnyMutation(
        "initiateTransactionFn",
        mutationData,
        "param",
        false,
        base_url
      );

      const isSuccess = response.results === "Transaction initiated successfully.";

      Swal.fire({
        icon: isSuccess ? "success" : "error",
        title: isSuccess ? "Success!" : "Error",
        text: response.results || "An unknown error occurred.",
      }).then(() => {
        if (isSuccess) {
          props.onClose();
          props.onAction();
          navigate("/manage-account-transfers"); // 👈 Navigate after closing modal and refreshing
        }
      });
  
    } catch (error) {
      Swal.fire("Error!", "An error occurred while processing your request. Please try again later.", "error");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        padding: "20px",
        borderRadius: "10px",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
        Initiate Transaction
      </Typography>

      <Divider sx={{ my: 2 }} />
      <form onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Transaction Type */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Transaction Type</InputLabel>
            <Select
              label="Transaction Type"
              value={selectedTransactionType}
              onChange={handleTransactionTypeChange}
              fullWidth
            >
              {transactionTypeData.map((transactionType) => (
                <MenuItem key={transactionType.Id} value={transactionType.Id}>
                  {transactionType.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Recipient Details */}
   {(selectedTransactionType === 1 || selectedTransactionType === 6 || selectedTransactionType === 7) && (
  <Grid item xs={12} sm={6}>
    <Box display="flex" alignItems="center" flexDirection="column">
      {/* Input for Mobile Number */}
      <TextField
        label="Recipient Mobile Number"
        value={recipientMobile}
        onChange={handleMobileChange}
        fullWidth
        required
        InputProps={{
          endAdornment: recipientId ? (
            <Tooltip title="Number exists" placement="top" arrow>
              <CheckCircleIcon color="success" />
            </Tooltip>
          ) : null,
        }}
        sx={{ mb: 2 }}
      />
        {!recipientOptions.length && recipientMobile.length < 10 && !LoadingSearch && (
            <Typography variant="body2" color="textSecondary">
              No recipients found for the entered mobile number.
            </Typography>
          )}
            {/* Display Selected Recipient Name */}
            {recipientName && (
              <Typography variant="body2" color="textSecondary" sx={{mb:-5}}>
                Selected Recipient: {recipientName}
              </Typography>
            )}
          </Box>
        </Grid>
      )}
       {selectedTransactionType === 3  && (
  <Grid item xs={12} sm={6}>
    <Box display="flex" alignItems="center" flexDirection="column">
      {/* Input for Mobile Number */}
      <TextField
        label="Recipient Mobile Number"
        value={recipientMobile}
        onChange={handleMobileChange}
        fullWidth
        required
        InputProps={{
          endAdornment: recipientId ? (
            <Tooltip title="Number exists" placement="top" arrow>
              <CheckCircleIcon color="success" />
            </Tooltip>
          ) : null,
        }}
        sx={{ mb: 2 }}
      />
        {!recipientOptions.length && recipientMobile.length < 10 && !LoadingSearch && (
            <Typography variant="body2" color="textSecondary">
              No recipients found for the entered mobile number.
            </Typography>
          )}
            {/* Display Selected Recipient Name */}
            {recipientName && (
              <Typography variant="body2" color="textSecondary" sx={{mb:-5}}>
                Selected Recipient: {recipientName}
              </Typography>
            )}
          </Box>
        </Grid>
      )}
       {selectedTransactionType === 4  && (
  <Grid item xs={12} sm={6}>
    <Box display="flex" alignItems="center" flexDirection="column">
      {/* Input for Mobile Number */}
      <TextField
        label="Recipient Mobile Number"
        value={recipientMobile}
        onChange={handleMobileChange}
        fullWidth
        required
        InputProps={{
          endAdornment: recipientId ? (
            <Tooltip title="Number exists" placement="top" arrow>
              <CheckCircleIcon color="success" />
            </Tooltip>
          ) : null,
        }}
        sx={{ mb: 2 }}
      />
        {!recipientOptions.length && recipientMobile.length < 10 && !LoadingSearch && (
            <Typography variant="body2" color="textSecondary">
              No recipients found for the entered mobile number.
            </Typography>
          )}
            {/* Display Selected Recipient Name */}
            {recipientName && (
              <Typography variant="body2" color="textSecondary" sx={{mb:-5}}>
                Selected Recipient: {recipientName}
              </Typography>
            )}
          </Box>
        </Grid>
      )}
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, color: theme.palette.text.primary }}>
        Amount
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            required
            sx={{ marginBottom: "15px" }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            // disabled={submitting || !recipientMobile ||}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {submitting ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </Grid>
      </Grid>
      </form>
    </Paper>
  );
};

export default AccountTransferForm;
